import { FunctionComponent, Fragment } from 'react';
import {
    Button,
    Card,
    CardContent,
    Divider,
    Fade,
    Typography,
} from '@mui/material';
import {
    BaseCondition,
    ContingentCondition,
    PriceCondition,
} from '@/models/product';
import { LoadingContainer } from '@/components/Layout/Container';
import { ContingentConditionPreview } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ContingentConditionPreview';
import { PriceConditionPreview } from './PriceConditionPreview';
import { BoldLabel } from '@/components/Layout/Textual';
import { ConditionPreviewContainer } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ConditionList';
const isOfTypeContingentCondition = (
    toBeDetermined: BaseCondition
): toBeDetermined is ContingentCondition => {
    return Object.prototype.hasOwnProperty.call(
        toBeDetermined,
        'maxTicketsPerStartDay'
    );
};

const sortConditions = (conditions: BaseCondition[]) =>
    conditions.sort((a, b) => a.sort - b.sort);

type ConditionPreviewList = {
    conditions: BaseCondition[];
    onCreateOrUpdate: (condition?: BaseCondition) => void;
    onDelete: (condition: BaseCondition) => void;
    onPriorityChange: (
        conditions: BaseCondition[],
        condition: BaseCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    onAddClick: () => void;
    isLoading?: boolean;
};

export const ConditionPreviewList: FunctionComponent<
    React.PropsWithChildren<ConditionPreviewList>
> = ({
    conditions,
    onCreateOrUpdate,
    onDelete,
    onPriorityChange,
    isLoading,
    onAddClick,
}) => {
    const conditionsSorted = sortConditions([...conditions]);

    const handlePriorityChange = (condition, priorityChange) =>
        onPriorityChange(conditionsSorted, condition, priorityChange);

    return (
        <Card
            sx={{
                position: 'relative',
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Typography variant='h6'>Angelegte Themenbereiche</Typography>

                <Button
                    sx={{
                        ml: 'auto',
                    }}
                    onClick={onAddClick}
                    variant='contained'
                >
                    + Neues Regelwerk
                </Button>
            </CardContent>

            <Divider
                sx={{
                    width: '100%',
                }}
            ></Divider>
            {conditionsSorted.map((condition, index) => {
                return (
                    <Fragment key={index}>
                        {isOfTypeContingentCondition(condition) ? (
                            <ContingentConditionPreview
                                condition={condition as ContingentCondition}
                                onCopy={onCreateOrUpdate}
                                onUpdate={onCreateOrUpdate}
                                onDelete={onDelete}
                                onPriorityChange={handlePriorityChange}
                                position={index + 1}
                                isFirst={index === 0}
                                isLast={index === conditions.length - 1}
                            />
                        ) : (
                            <PriceConditionPreview
                                condition={condition as PriceCondition}
                                onCopy={onCreateOrUpdate}
                                onUpdate={onCreateOrUpdate}
                                onDelete={onDelete}
                                onPriorityChange={handlePriorityChange}
                                position={index + 1}
                                isFirst={index === 0}
                                isLast={index === conditions.length - 1}
                            />
                        )}

                        {index < conditionsSorted.length - 1 && (
                            <Divider
                                sx={{
                                    width: '1ß0%',
                                }}
                            />
                        )}
                    </Fragment>
                );
            })}

            {!conditionsSorted.length && (
                <ConditionPreviewContainer
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    Sie haben noch keine Regelwerke erstellt. Fügen Sie welche
                    unter &nbsp;
                    <BoldLabel>&ldquo;+ Neues Regelwerk&rdquo;</BoldLabel>
                    &nbsp; hinzu.
                </ConditionPreviewContainer>
            )}

            <Fade in={isLoading}>
                <div>
                    <LoadingContainer cover />
                </div>
            </Fade>
        </Card>
    );
};
